import { useQuery } from '@tanstack/react-query';
import { CoinMarketCapTokenData } from '@crypto-trading/common/browser';
import { removeUsdtSuffix } from '@/utils/symbols';
import * as tokenMetadataApi from '@/api/tokenMetadata';

const TOKEN_METADATA_STALE_TIME_MS = 60 * 60 * 1000; // 1 hour
const TOKEN_METADATA_GC_TIME_MS = 24 * 60 * 60 * 1000; // 24 hours

export const tokenMetadataKeys = {
  all: ['tokenMetadata'] as const,
  detail: (symbol: string) => [...tokenMetadataKeys.all, symbol] as const,
};

/**
 * Hook to fetch token metadata from CoinMarketCap via our backend API
 * @param symbol - Token symbol (e.g., 'BTC', 'BTCUSDT')
 * @param enabled - Whether the query should run
 * @returns Query result with token metadata
 */
export const useTokenMetadata = (symbol: string, enabled = true) => {
  // Remove USDT suffix if present
  const baseSymbol = removeUsdtSuffix(symbol);

  return useQuery<CoinMarketCapTokenData | null, Error>({
    queryKey: tokenMetadataKeys.detail(baseSymbol),
    queryFn: () => tokenMetadataApi.getTokenMetadata(baseSymbol),
    enabled: enabled && !!baseSymbol,
    staleTime: TOKEN_METADATA_STALE_TIME_MS,
    gcTime: TOKEN_METADATA_GC_TIME_MS,
    retry: 1, // Only retry once for metadata
  });
};
